import { useMutation, useQuery } from '@apollo/client/react';
import {
  IntegrationDocument,
  IntegrationDocumentsPagePathSegment,
  IntegrationType,
  ProjectState,
  SourceType,
  UpdateSourceDocumentsDocumentInput,
} from '@sc/graphql';
import {
  Button,
  FullLoader,
  IconButton,
  Input,
  LinearLoader,
  Loader,
  Modal,
  ModalFooter,
} from '@sc/shared-ui';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { DocumentBrowserBreadcrumbs } from '../components/DocumentBrowserBreadcrumbs';
import {
  GQL_FRAGMENT_PROJECT,
  GQL_MUTATION_RESOLVE_INTEGRATION_SELECTION,
  GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_INTEGRATION_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCES,
} from '../utility/queries';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  DocumentIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import { useDebounce } from '../hooks/useDebounce';

interface Props {
  type: IntegrationType;
  sourceUuid: string;
  projectUuid: string;
  open: boolean;
  onClose: () => void;
}

const rootNamesForIntegrationTypes: Record<IntegrationType, string> = {
  [IntegrationType.dropbox]: 'Documents',
  [IntegrationType.googleDrive]: 'Documents',
  [IntegrationType.notion]: 'Data sources',
};

const sourceTypesForIntegrationTypes: Record<IntegrationType, SourceType> = {
  [IntegrationType.dropbox]: SourceType.dropbox,
  [IntegrationType.googleDrive]: SourceType.googleDrive,
  [IntegrationType.notion]: SourceType.notion,
};

// Search-based picker (vs. drill-by-path). Every integration must declare its
// mode, so a new one is a compile error rather than a silent default.
const supportsSearch: Record<IntegrationType, boolean> = {
  [IntegrationType.dropbox]: false,
  [IntegrationType.googleDrive]: false,
  [IntegrationType.notion]: true,
};

type SelectedDocType = IntegrationDocument & { isSelected: boolean };

const hostedId = (doc: { type: string; id: string }) => `${doc.type}#${doc.id}`;

const toCreatePayload = (
  doc: IntegrationDocument
): UpdateSourceDocumentsDocumentInput => ({
  title: doc.name,
  relevance: doc.relevance,
  hostedIntegrationDocumentPath: doc.pathSegments
    .map((i) => `${i.name}#${i.id}`)
    .join('/'),
  hostedIntegrationDocumentId: hostedId(doc),
  description: undefined,
  fileUuid: undefined,
  thumbnailUuid: undefined,
  url: undefined,
  hostedIntegrationMimeType: doc.mimeType,
});

// Used by the drill picker, whose selection lives in a `documents` collection.
const selectionToInput = (
  documents: SelectedDocType[],
  preselectedDocs: Array<{ id: string; path: string; uuid: string }>
): {
  documentsToCreate: UpdateSourceDocumentsDocumentInput[];
  documentsToDelete: string[];
} => ({
  documentsToCreate: documents
    .filter((d) => d.isSelected && !preselectedDocs.some((p) => p.id === d.id))
    .map(toCreatePayload),
  documentsToDelete: documents
    .filter((d) => preselectedDocs.some((p) => p.id === d.id) && !d.isSelected)
    .map((d) => {
      const doc = preselectedDocs.find((p) => p.id === d.id);
      if (!doc) throw new Error();
      return doc.uuid;
    }),
});

const DrillDocumentBrowser = ({
  type,
  setDocsToSave,
  setDocsToDelete,
  setDocumentCount,
  preselectedDocs,
}: Pick<Props, 'type'> & {
  setDocsToSave: Dispatch<SetStateAction<UpdateSourceDocumentsDocumentInput[]>>;
  setDocsToDelete: Dispatch<SetStateAction<string[]>>;
  setDocumentCount: Dispatch<SetStateAction<number>>;
  preselectedDocs: Array<{ id: string; path: string; uuid: string }>;
}) => {
  const [documents, setDocuments] = useState<SelectedDocType[]>([]);

  const applySelection = (next: SelectedDocType[]) => {
    setDocuments(next);

    const { documentsToCreate, documentsToDelete } = selectionToInput(
      next,
      preselectedDocs
    );

    setDocsToSave(documentsToCreate);

    setDocsToDelete(documentsToDelete);

    // No subtree resolution here — count the documents being added directly.
    setDocumentCount(documentsToCreate.length);
  };

  const [pathSegments, setPathSegments] = useState([
    { id: 'root', name: rootNamesForIntegrationTypes[type] },
  ] as IntegrationDocumentsPagePathSegment[]);

  const [nextPageToken, setNextPageToken] = useState<string | null>(null);

  const [isLoadingMore, startLoadMore] = useTransition();

  const { data, loading } = useQuery(GQL_QUERY_INDEX_INTEGRATION_DOCUMENTS, {
    variables: {
      input: {
        type,
        pathSegments,
        nextPageToken,
        query: undefined,
      },
    },
  });

  const activeDocuments = useMemo(() => {
    const lastSegment = pathSegments.at(-1);
    if (!lastSegment) throw new Error('Invalid segment');
    return documents
      .filter(
        (doc) => doc.pathSegments.map((i) => i.id).at(-1) === lastSegment.id
      )
      .map((d) => ({
        ...d,
        count:
          documents.filter(
            (i) =>
              i.isSelected && i.pathSegments.map((i) => i.id).includes(d.id)
          ).length +
          preselectedDocs.filter(
            (p) =>
              !documents.some((d) => d.id === p.id) && p.path.includes(d.id)
          ).length,
      }));
  }, [pathSegments, documents, preselectedDocs]);

  useEffect(() => {
    if (data) {
      setDocuments((docs) => [
        ...docs,
        ...data.indexIntegrationDocuments.items
          .filter((d) => !docs.some((doc) => doc.id === d.id))
          .map((d) => ({
            ...d,
            isSelected:
              docs.some(
                (i) =>
                  d.pathSegments.map((i) => i.id).includes(i.id) && i.isSelected
              ) || preselectedDocs.some((p) => p.id === d.id),
          })),
      ]);
    }
  }, [data, preselectedDocs, setDocuments]);

  const hasMorePages = !!data?.indexIntegrationDocuments.nextPageToken;

  // Keep the current folder's contents on screen while the next one loads, so
  // navigating doesn't blank the list and the modal height stays put.
  const [shownDocuments, setShownDocuments] = useState(activeDocuments);

  useEffect(() => {
    if (activeDocuments.length > 0 || !loading) {
      setShownDocuments(activeDocuments);
    }
  }, [activeDocuments, loading]);

  return (
    <div className=" bg-background-primary">
      <div className="border-b flex items-center space-x-2 px-3 py-2 border-grey-t-800">
        <IconButton
          Icon={ArrowLeftIcon}
          disabled={pathSegments.length === 1}
          onClick={() => {
            setNextPageToken(null);

            setPathSegments(pathSegments.slice(0, -1));
          }}
        />
        <DocumentBrowserBreadcrumbs
          pathSegments={pathSegments}
          onNavigate={(idx) => {
            setNextPageToken(null);

            setPathSegments(pathSegments.slice(0, idx + 1));
          }}
        />
      </div>
      <div className="h-100 flex flex-col grow overflow-hidden">
        {loading && <LinearLoader />}
        {!loading && data?.indexIntegrationDocuments.items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <RectangleStackIcon className="size-10 mb-3 text-grey-500" />
            <span className="text-body-medium font-medium text-white">
              No documents here
            </span>
            <span className="text-body-smallest mt-1 text-text-low-priority">
              This folder is empty
            </span>
          </div>
        ) : (
          <div className="overflow-y-auto">
            {shownDocuments.map((item) => (
              <div
                key={item.id}
                className="bg-background-secondary flex items-center px-3 py-2 space-x-3 border-b border-grey-t-800"
              >
                {item.hasChildren ? (
                  <FolderIcon className="size-4.5 shrink-0 text-grey-400" />
                ) : (
                  <DocumentIcon className="size-4.5 shrink-0 text-grey-400" />
                )}
                <span className="text-text-low-priority text-body-medium truncate min-w-0 flex-1">
                  {item.name}
                </span>
                {item.count > 0 && (
                  <div className="px-2 py-1 bg-[#70808E] rounded-full text-text-filled-component flex shrink-0">
                    <span className="text-xs leading-4 font-bold">
                      {item.count}
                    </span>
                  </div>
                )}
                <div className="shrink-0 flex items-center justify-end space-x-2">
                  {item.isSelected ? (
                    <Button
                      button={{
                        text: item.hasChildren ? 'Remove all' : 'Remove',
                        type: 'delete',
                        shrink: true,
                        size: 'small',
                        onClick: () =>
                          applySelection(
                            documents.map((s) => {
                              if (
                                s.id === item.id ||
                                (s.pathSegments
                                  .map((i) => i.id)
                                  .includes(item.id) &&
                                  item.hasChildren)
                              ) {
                                return {
                                  ...s,
                                  isSelected: false,
                                };
                              }
                              return s;
                            })
                          ),
                      }}
                    />
                  ) : (
                    <Button
                      button={{
                        text: item.hasChildren ? 'Add all' : 'Add',
                        type: 'basic',
                        shrink: true,
                        size: 'small',
                        onClick: () =>
                          applySelection(
                            documents.map((s) => {
                              if (
                                s.id === item.id ||
                                s.pathSegments
                                  .map((i) => i.id)
                                  .includes(item.id)
                              ) {
                                return {
                                  ...s,
                                  isSelected: true,
                                };
                              }
                              return s;
                            })
                          ),
                      }}
                    />
                  )}
                  {item.hasChildren && (
                    <IconButton
                      Icon={ArrowRightIcon}
                      tooltip="View documents"
                      type="primary"
                      size="small"
                      onClick={() => {
                        setNextPageToken(null);

                        setPathSegments((segs) => [
                          ...segs,
                          {
                            id: item.id,
                            name: item.name,
                            type: item.type,
                          },
                        ]);
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
            {hasMorePages && (
              <div className="flex justify-center py-2 border-b border-grey-t-800">
                <Button
                  button={{
                    text: isLoadingMore || loading ? 'Loading…' : 'Load more',
                    type: 'basic',
                    shrink: true,
                    size: 'small',
                    disabled: isLoadingMore || loading,
                    onClick: () =>
                      startLoadMore(() =>
                        setNextPageToken(
                          data.indexIntegrationDocuments.nextPageToken ?? null
                        )
                      ),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SearchDocumentBrowser = ({
  type,
  sourceUuid,
  docsToSave,
  setDocsToSave,
  docsToDelete,
  setDocsToDelete,
  preselectedDocs,
  setIsResolving,
  setDocumentCount,
}: Pick<Props, 'sourceUuid' | 'type'> & {
  docsToSave: UpdateSourceDocumentsDocumentInput[];
  setDocsToSave: Dispatch<SetStateAction<UpdateSourceDocumentsDocumentInput[]>>;
  docsToDelete: string[];
  setDocsToDelete: Dispatch<SetStateAction<string[]>>;
  preselectedDocs: Array<{ id: string; path: string; uuid: string }>;
  setIsResolving: Dispatch<SetStateAction<boolean>>;
  setDocumentCount: Dispatch<SetStateAction<number>>;
}) => {
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce(query, 300);

  const hasQuery = debouncedQuery.trim().length > 0;

  const {
    data,
    loading: isLoading,
    refetch,
    fetchMore,
  } = useQuery(GQL_QUERY_INDEX_INTEGRATION_DOCUMENTS, {
    variables: {
      input: {
        type,
        pathSegments: [],
        query: debouncedQuery,
        nextPageToken: null,
      },
    },
    skip: !hasQuery,
    notifyOnNetworkStatusChange: true,
  });

  const nextPageToken = data?.indexIntegrationDocuments.nextPageToken;

  // Load the next page; the cache `merge` policy appends it onto `data.items`.
  const loadMore = () =>
    void fetchMore({
      variables: {
        input: {
          type,
          pathSegments: [],
          query: debouncedQuery,
          nextPageToken,
        },
      },
    });

  const [resolve] = useMutation(GQL_MUTATION_RESOLVE_INTEGRATION_SELECTION);

  // Ids whose subtree is being resolved + cached. Shown as a per-row spinner and
  // bubbled up so Save stays disabled until every selection has resolved.
  const [resolving, setResolving] = useState<string[]>([]);

  // Resolved document count per selection (subtree size), keyed by item id.
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    setIsResolving(resolving.length > 0);
  }, [resolving, setIsResolving]);

  // Total documents the current selection will add, surfaced on the Save button.
  const documentCount = useMemo(
    () =>
      docsToSave.reduce((sum, d) => {
        const id = d.hostedIntegrationDocumentId?.split('#')[1];

        return sum + (id ? counts[id] ?? 0 : 0);
      }, 0),
    [docsToSave, counts]
  );

  useEffect(() => {
    setDocumentCount(documentCount);
  }, [documentCount, setDocumentCount]);

  const preselectedUuid = (item: IntegrationDocument) =>
    preselectedDocs.find((p) => p.id === item.id)?.uuid;

  const onAdd = async (item: IntegrationDocument) => {
    const savedUuid = preselectedUuid(item);

    if (savedUuid) {
      setDocsToDelete((ids) => ids.filter((uuid) => uuid !== savedUuid));
    } else {
      setDocsToSave((docs) =>
        docs.some((d) => d.hostedIntegrationDocumentId === hostedId(item))
          ? docs
          : [...docs, toCreatePayload(item)]
      );
    }

    setResolving((r) => [...r, item.id]);

    try {
      const res = await resolve({
        variables: {
          input: { sourceUuid, type, id: item.id, documentType: item.type },
        },
      });

      setCounts((c) => ({
        ...c,
        [item.id]: res.data?.resolveIntegrationSelection.count ?? 0,
      }));
    } catch {
      // swallowed — re-resolved at index time
    } finally {
      setResolving((r) => r.filter((id) => id !== item.id));
    }
  };

  const onRemove = (item: IntegrationDocument) => {
    const savedUuid = preselectedUuid(item);

    if (savedUuid) {
      setDocsToDelete((ids) =>
        ids.includes(savedUuid) ? ids : [...ids, savedUuid]
      );
    } else {
      setDocsToSave((docs) =>
        docs.filter((d) => d.hostedIntegrationDocumentId !== hostedId(item))
      );
    }

    setCounts((c) => {
      const next = { ...c };

      delete next[item.id];

      return next;
    });
  };

  const results = useMemo(
    () =>
      (data?.indexIntegrationDocuments.items ?? [])
        .map((item) => {
          const savedUuid = preselectedDocs.find((p) => p.id === item.id)?.uuid;

          return {
            ...item,
            isSelected: savedUuid
              ? !docsToDelete.includes(savedUuid)
              : docsToSave.some(
                  (d) => d.hostedIntegrationDocumentId === hostedId(item)
                ),
            isResolving: resolving.includes(item.id),
            count: counts[item.id],
          };
        })
        .sort((a, b) =>
          a.type === b.type ? 0 : a.type === 'data_source' ? -1 : 1
        ),
    [data, resolving, counts, preselectedDocs, docsToSave, docsToDelete]
  );

  const isSearching =
    query.trim().length > 0 &&
    (isLoading || query.trim() !== debouncedQuery.trim());

  return (
    <div className="bg-background-primary">
      <div className="border-b flex items-center space-x-2 px-3 py-2 border-grey-t-800">
        <Input
          className="flex-1"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your Notion…"
          Icon={MagnifyingGlassIcon}
        />
        <IconButton
          Icon={ArrowPathIcon}
          tooltip="Refresh"
          type="tertiary"
          size="large"
          className="h-11 w-11 shrink-0 flex items-center justify-center"
          onClick={() => {
            if (!query.trim()) {
              notify.info('Please enter a search term');

              return;
            }

            // Reload from Notion with exactly what's typed (bypassing the
            // debounce), forcing a fresh fetch even when the term is unchanged.
            void refetch({
              input: {
                type,
                pathSegments: [],
                query,
                nextPageToken: null,
              },
            });
          }}
        />
      </div>

      <div className="h-100 flex flex-col grow overflow-hidden">
        {isSearching && <LinearLoader />}
        {!query.trim() ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <RectangleStackIcon className="size-10 mb-3 text-grey-500" />
            <span className="text-body-medium font-medium text-white">
              Search your Notion
            </span>
            <span className="text-body-smallest mt-1 text-text-low-priority">
              Find pages and data sources to add as sources
            </span>
          </div>
        ) : results.length > 0 ? (
          <div className="overflow-y-auto">
            {results.map((item) => (
              <div
                key={item.id}
                className="bg-background-secondary flex items-center px-3 py-2 space-x-3 border-b border-grey-t-800"
              >
                {item.type === 'data_source' ? (
                  <FolderIcon className="size-4.5 shrink-0 text-grey-400" />
                ) : (
                  <DocumentIcon className="size-4.5 shrink-0 text-grey-400" />
                )}
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <span className="text-text-low-priority text-body-medium truncate min-w-0">
                    {item.name}
                  </span>
                  {item.isSelected && item.count !== undefined && (
                    <div className="shrink-0 px-2 py-1 rounded-full flex items-center justify-center border border-secondary-400 text-text-low-priority">
                      <span className="text-xs leading-4 font-bold">
                        {item.count}
                      </span>
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center justify-end space-x-2 min-h-9">
                  {item.isResolving ? (
                    <Loader width={16} height={16} />
                  ) : item.isSelected ? (
                    <IconButton
                      Icon={TrashIcon}
                      type="delete"
                      size="small"
                      tooltip="Remove"
                      onClick={() => onRemove(item)}
                    />
                  ) : (
                    <Button
                      button={{
                        text: 'Add',
                        type: 'basic',
                        shrink: true,
                        size: 'small',
                        onClick: () => void onAdd(item),
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
            {nextPageToken && (
              <div className="flex justify-center py-2 border-b border-grey-t-800">
                <Button
                  button={{
                    text: isLoading ? 'Loading…' : 'Load more',
                    type: 'basic',
                    shrink: true,
                    size: 'small',
                    disabled: isLoading,
                    onClick: loadMore,
                  }}
                />
              </div>
            )}
          </div>
        ) : isSearching ? (
          <div className="flex flex-1 items-center justify-center">
            <FullLoader className="size-5" />
          </div>
        ) : (
          <div className="py-10 px-6 text-center text-text-low-priority text-body-medium">
            No results for “{debouncedQuery}”
          </div>
        )}
      </div>
    </div>
  );
};

const InnerDocuments = ({
  type,
  sourceUuid,
  projectUuid,
  onClose,
  setLoading,
}: Omit<Props, 'open'> & { setLoading: Dispatch<SetStateAction<boolean>> }) => {
  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const { data: sourceDocuments } = useQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
    {
      variables: {
        input: {
          filter: {
            sourceUuid,
          },
          pagination: {
            perPage: 1000,
            page: 1,
          },
        },
      },
    }
  );

  const [docsToSave, setDocsToSave] = useState<
    UpdateSourceDocumentsDocumentInput[]
  >([]);

  const [docsToDelete, setDocsToDelete] = useState<string[]>([]);

  const [isResolving, setIsResolving] = useState(false);

  const [documentCount, setDocumentCount] = useState(0);

  const [save, { loading }] = useMutation(
    GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
    {
      onError,
      refetchQueries: [
        GQL_QUERY_INDEX_SOURCES,
        GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
        GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
      ],
      update: (cache) =>
        cache.updateFragment(
          {
            id: `Project:${projectUuid}`,
            fragment: GQL_FRAGMENT_PROJECT,
          },
          (d) =>
            d
              ? {
                  ...d,
                  state: ProjectState.draft,
                  processedPc: 0,
                  sourceCount: d.sourceCount + 1,
                }
              : d
        ),
      onCompleted: () => {
        track('Source Documents Updated', {
          sourceType: sourceTypesForIntegrationTypes[type],
          projectUuid,
          documentsCreated: docsToSave.length,
          documentsDeleted: docsToDelete.length,
        });

        notify.success('Saved source.');

        setDocsToSave([]);

        setDocsToDelete([]);

        onClose();
      },
    }
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const preselectedDocs = useMemo(
    () =>
      sourceDocuments?.indexSourceDocuments.items
        .map((s) => ({
          path: s.hostedIntegrationPath,
          id: s.hostedIntegrationDocumentId?.split('#')[1],
          uuid: s.uuid,
        }))
        .filter(
          (s): s is { path: string; id: string; uuid: string } =>
            !!(s.path && s.id && s.uuid)
        ) ?? [],
    [sourceDocuments?.indexSourceDocuments.items]
  );

  return (
    <>
      {supportsSearch[type] ? (
        <SearchDocumentBrowser
          preselectedDocs={preselectedDocs}
          docsToSave={docsToSave}
          setDocsToSave={setDocsToSave}
          docsToDelete={docsToDelete}
          setDocsToDelete={setDocsToDelete}
          setIsResolving={setIsResolving}
          setDocumentCount={setDocumentCount}
          sourceUuid={sourceUuid}
          type={type}
        />
      ) : (
        <DrillDocumentBrowser
          preselectedDocs={preselectedDocs}
          setDocsToSave={setDocsToSave}
          setDocsToDelete={setDocsToDelete}
          setDocumentCount={setDocumentCount}
          type={type}
        />
      )}
      <ModalFooter
        onClose={onClose}
        submitText={
          documentCount > 0
            ? `Add ${documentCount} document${documentCount === 1 ? '' : 's'}`
            : 'Save documents'
        }
        buttonsDisabled={loading || isResolving}
        onSubmit={() =>
          void save({
            variables: {
              input: {
                uuid: sourceUuid,
                integrationType: type,
                documentsToCreate: docsToSave,
                documentsToDelete: docsToDelete,
              },
            },
          })
        }
      />
    </>
  );
};

export const DocumentBrowser = ({
  type,
  projectUuid,
  sourceUuid,
  open,
  onClose,
}: Props) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open)
      track('Document Browser Opened', { integrationType: type, projectUuid });
  }, [open, type, projectUuid]);

  return (
    <Modal
      title="Select documents"
      dialogPanelClassname="w-[480px]"
      open={open}
      onClose={onClose}
      loading={loading}
    >
      <InnerDocuments
        setLoading={setLoading}
        type={type}
        sourceUuid={sourceUuid}
        projectUuid={projectUuid}
        onClose={onClose}
      />
    </Modal>
  );
};
